import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

export default function HolidaysPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("all");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const canEdit = ["admin", "receptionist"].includes(user?.role);

  const fetchData = async () => {
    const qp = new URLSearchParams();
    if (scope !== "all") qp.append("scope", scope);
    if (year !== "all") qp.append("year", year);
    const { data } = await http.get(`/holidays?${qp.toString()}`);
    setItems(data.items || []);
  };
  useEffect(() => { fetchData(); }, [scope, year]); // eslint-disable-line

  return (
    <PageChrome title={t("holidays.title")} subtitle={t("holidays.subtitle")}>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-[160px] bg-white" data-testid="holidays-filter-scope"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="national">{t("holidays.scope_national")}</SelectItem>
            <SelectItem value="local">{t("holidays.scope_local")}</SelectItem>
            <SelectItem value="academy">{t("holidays.scope_academy")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[120px] bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {[2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          {canEdit && <Button onClick={() => setCreating(true)} className="bg-[#0A1F44]" data-testid="holidays-add-button"><Plus className="h-4 w-4 mr-2"/>{t("holidays.add")}</Button>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-3">{t("holidays.date")}</th>
              <th className="p-3">{t("holidays.name")}</th>
              <th className="p-3">{t("holidays.scope")}</th>
              <th className="p-3">{t("holidays.recurring")}</th>
              <th className="p-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">{t("common.noResults")}</td></tr>}
            {items.map(h => (
              <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`holiday-row-${h.id}`}>
                <td className="p-3 font-mono">{h.date}</td>
                <td className="p-3 font-medium">{h.name}</td>
                <td className="p-3"><Badge variant="secondary">{h.scope}</Badge></td>
                <td className="p-3">{h.recurring_annual ? "✓" : ""}</td>
                <td className="p-3 text-right space-x-1">
                  {canEdit && <>
                    <Button variant="ghost" size="icon" onClick={() => setEditing(h)} data-testid={`holiday-edit-${h.id}`}><Pencil className="h-3.5 w-3.5"/></Button>
                    <Button variant="ghost" size="icon" onClick={async () => {
                      if (!window.confirm(t("holidays.deleteConfirm"))) return;
                      await http.delete(`/holidays/${h.id}`);
                      toast.success("Deleted"); fetchData();
                    }} data-testid={`holiday-delete-${h.id}`}><Trash2 className="h-3.5 w-3.5 text-[#C8102E]"/></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <HolidayEdit
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchData(); }}
        />
      )}
    </PageChrome>
  );
}

function HolidayEdit({ initial, onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState(initial || { date: "", name: "", scope: "local", recurring_annual: false });
  const submit = async () => {
    try {
      if (initial?.id) await http.patch(`/holidays/${initial.id}`, f);
      else await http.post(`/holidays`, f);
      toast.success(t("holidays.saved")); onSaved();
    } catch (e) { toast.error("Save failed"); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {initial ? t("holidays.edit") : t("holidays.create")}
        </DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>{t("holidays.date")} *</Label><Input type="date" value={f.date || ""} onChange={e => setF({ ...f, date: e.target.value })} data-testid="holiday-form-date"/></div>
          <div><Label>{t("holidays.name")} *</Label><Input value={f.name || ""} onChange={e => setF({ ...f, name: e.target.value })} data-testid="holiday-form-name"/></div>
          <div><Label>{t("holidays.scope")}</Label>
            <Select value={f.scope || "local"} onValueChange={v => setF({ ...f, scope: v })}>
              <SelectTrigger data-testid="holiday-form-scope"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="national">{t("holidays.scope_national")}</SelectItem>
                <SelectItem value="local">{t("holidays.scope_local")}</SelectItem>
                <SelectItem value="academy">{t("holidays.scope_academy")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={!!f.recurring_annual} onCheckedChange={v => setF({ ...f, recurring_annual: !!v })} data-testid="holiday-form-recurring"/>
            <span>{t("holidays.recurring")}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} className="bg-[#0A1F44]" data-testid="holiday-form-save">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
